import { DASHBOARD_API_BASE_URL, USE_DASHBOARD_REAL_BACKEND } from './config/config'
import {
  ADMIN_REFRESH_TOKEN_KEY,
  ADMIN_TOKEN_KEY,
  clearAdminSession,
} from '../auth/adminAuth'

const originalFetch = window.fetch.bind(window)
const refreshUrl =
  import.meta.env.VITE_ADMIN_REFRESH_URL || `${DASHBOARD_API_BASE_URL}/api/token/refresh/`

const isAdminRequest = (input: RequestInfo | URL): boolean => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  return url.startsWith(DASHBOARD_API_BASE_URL) &&
    !url.endsWith('/api/admin/login') &&
    !url.endsWith('/api/token/refresh/') &&
    !url.endsWith('/api/auth/logout')
}

const getAccessToken = (value: unknown): string | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const response = value as Record<string, any>
  return response.access || response.access_token || response.token || response.data?.access
}

export const refreshAdminAccessToken = async (): Promise<string | null> => {
  const refresh = localStorage.getItem(ADMIN_REFRESH_TOKEN_KEY)
  if (!refresh) return null

  const response = await originalFetch(refreshUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  }).catch(() => null)

  if (!response || !response.ok) return null

  const result = await response.json().catch(() => null)
  const access = getAccessToken(result)
  if (!access) return null

  localStorage.setItem(ADMIN_TOKEN_KEY, access)
  return access
}

const redirectToLogin = () => {
  clearAdminSession()
  if (window.location.pathname !== '/admin-login') {
    window.location.assign('/admin-login')
  }
}

window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const request = new Request(input, init)
  if (!USE_DASHBOARD_REAL_BACKEND || !isAdminRequest(request)) {
    return originalFetch(request)
  }

  const retryRequest = request.clone()
  const token = localStorage.getItem(ADMIN_TOKEN_KEY)
  if (token && !request.headers.has('Authorization')) {
    request.headers.set('Authorization', `Bearer ${token}`)
  }

  const response = await originalFetch(request)
  if (response.status !== 401 || !token) {
    return response
  }

  const refreshedToken = await refreshAdminAccessToken()
  if (!refreshedToken) {
    redirectToLogin()
    return response
  }

  const retry = new Request(retryRequest)
  retry.headers.set('Authorization', `Bearer ${refreshedToken}`)
  return originalFetch(retry)
}
